/**
 * Custom hook for managing feedback dialog state
 */

import { useState, useCallback, FormEvent } from 'react';
import { submitFeedback, FeedbackRequest } from 'services/apiService';

export function useFeedback(
  apiAddress: string | null | undefined,
  token: string | undefined,
  onSuccess?: () => void
) {
  const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);
  const [feedbackType, setFeedbackType] = useState<string>('positive');
  const [feedbackContent, setFeedbackContent] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openPositiveFeedback = useCallback(() => {
    setFeedbackType("positive");
    setOpenFeedbackDialog(true);
  }, []);

  const openNegativeFeedback = useCallback(() => {
    setFeedbackType("negative");
    setOpenFeedbackDialog(true);
  }, []);

  const closeFeedbackDialog = useCallback(() => {
    setOpenFeedbackDialog(false);
    setFeedbackContent('');
  }, []);

  const handleSubmitFeedback = useCallback(async (
    event: FormEvent,
    requestData: Omit<FeedbackRequest, 'Type' | 'Content'>
  ) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      await submitFeedback(
        {
          Type: feedbackType,
          Content: feedbackContent,
          ...requestData
        },
        token,
        apiAddress
      );

      setFeedbackType('');
      setFeedbackContent('');
      setOpenFeedbackDialog(false);
      onSuccess?.();
    } catch (error) {
      console.error('Feedback submission error:', error);
      alert(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [feedbackType, feedbackContent, token, apiAddress, onSuccess]);

  return {
    openFeedbackDialog,
    feedbackType,
    feedbackContent,
    isSubmitting,
    setFeedbackContent,
    setOpenFeedbackDialog,
    openPositiveFeedback,
    openNegativeFeedback,
    closeFeedbackDialog,
    handleSubmitFeedback
  };
}
